import clsx from 'clsx';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';

// Hooks
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';
import useRouterQuery from '@/utils/hooks/utils/useRouterQuery';
import { useNewFeatureNotifications } from '@/utils/hooks/user/useNewFeatureNotifications';

// Type
import type { SocialProfileNetworkType } from '@/types/socialProfileType';

// Components
import Icon from '@/components/base/Icon';
import Badge from '@/components/base/Badge';

export type TabsLinkListItemType = {
    id: string;
    name: string;
    url: string;
    icon?: string;
    socialProfileRestriction?: SocialProfileNetworkType[];
};

export type TabsLinkProps = {
    tabsList: TabsLinkListItemType[];
};

const TabsLink = ({ tabsList }: TabsLinkProps) => {
    const { t } = useTranslation();
    const { deleteAllQueries } = useRouterQuery();
    const currentUrlDeletedQueries = deleteAllQueries();
    const currentUrl = currentUrlDeletedQueries.split('#')[0];
    const { activeSocialProfile } = useActiveSocialProfile();
    const { isNewFeatureOnPages } = useNewFeatureNotifications();
    return (
        <div role="tablist" className="tab-list flex flex-wrap items-center gap-x-8 gap-y-2">
            {tabsList.map((tab) => {
                if (
                    'socialProfileRestriction' in tab &&
                    !tab.socialProfileRestriction.includes(activeSocialProfile.social_network_type)
                ) {
                    return null;
                }

                return (
                    <Link
                        key={tab.id}
                        href={tab.url}
                        role="tab"
                        className="flex items-center space-x-2.5 font-GBold text-3.5xl">
                        <span className="flex items-center space-x-3">
                            <Icon
                                className={clsx('text-xl', {
                                    'text-icoBlue': currentUrl === tab.url,
                                    'opacity-30': currentUrl !== tab.url
                                })}
                                icon={tab.icon}
                            />
                            <span className={clsx({ 'opacity-30': currentUrl !== tab.url })}>
                                {t(tab.name)}
                            </span>
                        </span>
                        {isNewFeatureOnPages([tab.url]) ? <Badge variant="new" /> : null}
                    </Link>
                );
            })}
        </div>
    );
};

export default TabsLink;
